import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import SoundToggle from "./components/SiteSound";
import { AppProvider } from "./context/AppContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileBookBar from "./components/MobileBookBar";
import BookingModal from "./components/BookingModal";
import HomePage from "./pages/HomePage";
import InvestorsPage from "./pages/InvestorsPage";
import AdminPage from "./pages/AdminPage";
import LegalPage from "./pages/LegalPage";
import LoginPage from "./pages/LoginPage";
import PartnersPage from "./pages/PartnersPage";
import ForBusinessPage from "./pages/ForBusinessPage";
import AggregatorPage from "./pages/AggregatorPage";
import BookingDetailsPage from "./pages/BookingDetailsPage";
import VendorPage from "./pages/VendorPage";
import PartnerPage from "./pages/PartnerPage";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink px-6 text-center">
          <span className="font-logo text-sm tracking-[0.2em] text-paper">CARZA</span>
          <p className="text-mist">Something went wrong. Please refresh the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
  return null;
}

function ScrollManager() {
  const { pathname, state } = useLocation();
  useEffect(() => {
    if (state?.scrollTo) {
      const t = setTimeout(() => {
        const el = document.getElementById(state.scrollTo);
        if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -76 }) : el.scrollIntoView();
      }, 350);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, state]);
  return null;
}

function SiteChrome() {
  return (
    <>
      <Navbar />
      <MobileBookBar />
      <BookingModal />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollManager />
          <div className="grain min-h-screen bg-ink text-paper">
            <Routes>
              <Route path="/" element={<><HomePage /><SiteChrome /></>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/partners" element={<><PartnersPage /><SiteChrome /></>} />
              <Route path="/for-business" element={<><ForBusinessPage /><SiteChrome /></>} />
              <Route path="/aggregator" element={<><AggregatorPage /><SiteChrome /></>} />
              <Route path="/booking/:ref" element={<><BookingDetailsPage /><SiteChrome /></>} />
              <Route path="/vendor" element={<VendorPage />} />
              <Route path="/partner" element={<PartnerPage />} />
              <Route path="/investors" element={<><InvestorsPage /><SiteChrome /></>} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<><HomePage /><SiteChrome /></>} />
            </Routes>
          </div>
          <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#20133D", border: "1px solid rgba(247,245,251,0.1)", color: "#F7F5FB" } }} />
          <SoundToggle />
        </BrowserRouter>
      </AppProvider>
    </ErrorBoundary>
  );
}
